import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Space, Typography } from 'antd';
import { EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getResidents } from '../../api/residents';
import { getResidentDocuments } from '../../api/documents';
import { Resident } from '../../types/resources/resident';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

interface ResidentDocumentsTabProps {
  onViewDocuments: (residentId: string, residentName: string, ownerType: 'resident') => void;
}

const ResidentDocumentsTab: React.FC<ResidentDocumentsTabProps> = ({ onViewDocuments }) => {
  const { t } = useTranslation();
  const { firestoreUser } = useAuth();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch residents data
  const { data: residentsData, isLoading: residentsLoading } = useQuery({
    queryKey: ['residents', pagination.current, pagination.pageSize, firestoreUser?.workspace?.id],
    queryFn: () => getResidents({
      page: pagination.current,
      pageSize: pagination.pageSize,
      firestoreUser: firestoreUser!,
    }),
    enabled: !!firestoreUser?.workspace?.id,
  });

  // Fetch resident documents data
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['residentDocuments', firestoreUser?.workspace?.id],
    queryFn: () => getResidentDocuments(firestoreUser?.workspace?.id || ''),
    enabled: !!firestoreUser?.workspace?.id,
  });

  // Create a map of resident documents count
  const documentsCountMap = useMemo(() => {
    if (!documentsData?.data) return new Map();
    
    const countMap = new Map<string, number>();
    documentsData.data.forEach((doc: any) => {
      // Check if this is a resident document by looking for residentId property
      if (doc.residentId) {
        const count = countMap.get(doc.residentId) || 0;
        countMap.set(doc.residentId, count + 1);
      }
    });
    
    return countMap;
  }, [documentsData]);

  const columns = useMemo(() => [
    {
      title: t('residents:table.displayName'),
      dataIndex: 'user',
      key: 'displayName',
      render: (user: any) => (
        <Space direction="vertical" size="small">
          <Text strong>{user?.displayName || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {user?.phoneNumber || 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: t('residents:table.block'),
      dataIndex: 'block',
      key: 'block',
      render: (block: any) => (
        <Tag color="blue">{block?.name || 'N/A'}</Tag>
      ),
    },
    {
      title: t('residents:table.apartmentUnit'),
      dataIndex: 'apartmentUnit',
      key: 'apartmentUnit',
      render: (apartmentUnit: any) => (
        <Tag color="green">{apartmentUnit?.number || 'N/A'}</Tag>
      ),
    },
    {
      title: t('documents:table.documents'),
      key: 'documents',
      render: (_: any, record: Resident) => {
        const documentCount = documentsCountMap.get(record.id) || 0;
        return (
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <Text>{documentCount} {t('documents:table.document', { count: documentCount })}</Text>
          </Space>
        );
      },
    },
    {
      title: t('residents:table.actions'),
      key: 'actions',
      render: (_: any, record: Resident) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => onViewDocuments(record.id, record.user?.displayName || 'N/A', 'resident')}
          size="small"
        >
          {t('documents:actions.viewDocuments')}
        </Button>
      ),
    },
  ], [t, documentsCountMap]);

  const handleTableChange = (pagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    }));
  };

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>{t('documents:tabs.residents')}</Title>
        <Text type="secondary">
          {t('documents:tabs.descriptions.residents')}
        </Text>
      </div>
      
      <Table
        columns={columns}
        dataSource={residentsData?.residents || []}
        loading={residentsLoading || documentsLoading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: residentsData?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common:pagination.showing', { from: range[0], to: range[1], total }),
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default ResidentDocumentsTab;
